import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Arrow, ChevronDown, Mark } from "./icons";
import { productCategories } from "~/config/products";
import { getPurchaseUrl } from "~/config/purchase";
import { AiAssistant } from "./ai-assistant";

const nav = [
  { to: "/", label: "Home" },
  { to: "/knowledge-base", label: "Help centre" },
  { to: "/blog", label: "Journal" },
];
const categories = productCategories
  .filter((category) => category.showInMenu)
  .sort((a, b) => a.order - b.order);

export function Header() {
  const cta = {
    label: "Get hosting",
    href: getPurchaseUrl({ product: "webHosting" }),
  };
  return (
    <header className="sticky top-0 z-30 border-b border-ink/10 bg-cream/92 backdrop-blur-xl">
      <div className="relative mx-auto flex h-18 max-w-7xl items-center justify-between px-5 lg:px-12">
        <Link
          to="/"
          className="flex items-center gap-3 font-bold tracking-[-0.04em]"
        >
          <Mark />
          <span className="text-xl">HostRight</span>
        </Link>
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-4 text-sm font-medium md:flex">
          <Link
            to="/"
            activeProps={{ className: "border-moss/60 text-moss" }}
            className="border-b-2 border-transparent px-2 py-1 transition hover:border-moss/40 hover:text-moss"
          >
            Home
          </Link>
          <ProductsMenu />
          {nav.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeProps={{ className: "border-moss/60 text-moss" }}
              className="border-b-2 border-transparent px-2 py-1 transition hover:border-moss/40 hover:text-moss"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={cta.href}
            target="_blank"
            rel="noreferrer"
            className="hidden rounded-full border border-ink/15 bg-transparent px-5 py-2.5 text-sm font-bold text-ink transition hover:border-moss/40 hover:bg-white motion-safe:hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3 md:inline-flex"
          >
            {cta.label}
          </a>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}

function ProductsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  return (
    <div ref={ref} className="relative">
      <div
        className={`flex items-center gap-1 px-2 py-1 transition ${open ? "text-moss" : "hover:text-moss"}`}
        onMouseEnter={() => setOpen(true)}
      >
        <Link to="/products" onClick={() => setOpen(false)} className="py-1">
          Products
        </Link>
        <button
          type="button"
          aria-label="Open products menu"
          aria-expanded={open}
          aria-controls="products-menu"
          onClick={() => setOpen((value) => !value)}
          className="grid size-6 place-items-center"
        >
          <ChevronDown />
        </button>
      </div>
      {open && (
        <div
          id="products-menu"
          className="absolute left-0 top-[calc(100%+1rem)] w-88 rounded-2xl border border-ink/10 bg-cream p-3 shadow-[0_18px_45px_rgba(16,37,31,.18)]"
        >
          <div className="flex items-center justify-between px-2 pb-3">
            <p className="text-sm font-bold">Browse products</p>
            <Link
              to="/products"
              onClick={() => setOpen(false)}
              className="text-xs font-bold text-moss hover:underline"
            >
              View all
            </Link>
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto border-t border-ink/10 pt-2">
            {categories.map((category) => (
              <Link
                key={category.id}
                to="/products/$category"
                params={{ category: category.slug }}
                onClick={() => setOpen(false)}
                className="group flex items-center justify-between rounded-xl px-3 py-3 transition hover:bg-cloud"
              >
                <div>
                  <p className="text-sm font-bold">{category.navLabel}</p>
                  <p className="mt-1 text-xs text-ink/55">
                    {category.products.length}{" "}
                    {category.products.length === 1 ? "service" : "services"}
                  </p>
                </div>
                <Arrow />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMenu() {
  return (
    <details className="relative md:hidden">
      <summary className="list-none cursor-pointer rounded-full border border-ink/15 px-4 py-2 text-sm font-bold [&::-webkit-details-marker]:hidden">
        Menu
      </summary>
      <div className="absolute right-0 top-[calc(100%+1rem)] w-[min(22rem,calc(100vw-2.5rem))] rounded-3xl border border-ink/10 bg-cream p-5 shadow-[0_22px_55px_rgba(16,37,31,.18)]">
        <div className="grid gap-4 text-sm font-bold">
          <a href="/">Home</a>
          <details>
            <summary className="cursor-pointer">Products</summary>
            <div className="mt-3 grid gap-3 border-l border-ink/10 pl-4">
              {categories.map((category) => (
                <a
                  key={category.id}
                  href={`/products/${category.slug}`}
                  className="text-moss"
                >
                  {category.navLabel}
                </a>
              ))}
              <a href="/products">All products</a>
            </div>
          </details>
          <a href="/knowledge-base">Help centre</a>
          <a href="/blog">Journal</a>
          <a
            href={getPurchaseUrl({ product: "webHosting" })}
            target="_blank"
            rel="noreferrer"
            className="button-primary rounded-full px-4 py-3 text-center"
          >
            Get hosting
          </a>
        </div>
      </div>
    </details>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-ink/10 bg-ink text-cream">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-12 pt-20 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:px-12">
        <div>
          <div className="flex items-center gap-3 font-bold tracking-[-0.04em]">
            <Mark /> <span className="text-xl">HostRight</span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-6 text-cream/75">
            Straightforward web hosting for the websites, shops, and ideas that
            deserve a dependable home.
          </p>
          <p className="mt-5 text-xs font-bold tracking-wide text-leaf">
            US • EU •{" "}
            <a
              href="https://status.hostright.de"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-cream"
            >
              PUBLIC STATUS PAGE
            </a>
          </p>
        </div>
        <FooterColumn
          title="Explore"
          links={[
            ["Shared hosting", "/products/web-hosting/shared-hosting"],
            ["All products", "/products"],
          ]}
        />
        <FooterColumn
          title="Learn"
          links={[
            ["Knowledge base", "/knowledge-base"],
            ["Journal", "/blog"],
            ["Getting started", "/knowledge-base/getting-started"],
            ["Contact", "/contact"],
          ]}
        />
        <FooterColumn
          title="Legal"
          links={[
            ["Terms of service", "/legal/terms-of-service"],
            ["Fair-use policy", "/legal/fair-use-policy"],
            ["Privacy policy", "/legal/privacy-policy"],
          ]}
        />
      </div>
      <div className="border-t border-cream/10 px-5 py-5 text-center text-xs text-cream/50">
        © {new Date().getFullYear()} HostRight, a Brand of Sauri Labs. All
        rights reserved.
      </div>
    </footer>
  );
}
function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: [string, string][];
}) {
  return (
    <div>
      <p className="eyebrow text-leaf">{title}</p>
      <div className="mt-4 grid gap-3 text-sm text-cream/80">
        {links.map(([label, to]) => (
          <Link
            key={to}
            to={to}
            className="transition hover:text-leaf focus-visible:outline-2 focus-visible:outline-leaf focus-visible:outline-offset-3"
          >
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}
export function Shell({
  children,
  hideAiAssistant = false,
}: {
  children: ReactNode;
  hideAiAssistant?: boolean;
}) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      {!hideAiAssistant && <AiAssistant floating />}
    </>
  );
}
